/**
 * Timer Manager
 *
 * Handles AFK and Ad timer logic for the Quickstart flow.
 * Manages countdown timers, cumulative time tracking, and timer callbacks.
 *
 * This is feature-specific to Quickstart and knows about:
 * - AFK timer behavior (reset vs cumulative)
 * - Ad timer (playtime limit)
 * - Theme-configured timeouts
 */

import type { QuickstartState } from '../QuickstartController';

export interface TimerManagerConfig {
  /**
   * Get current state
   */
  getState: () => QuickstartState;

  /**
   * Update state
   */
  updateState: (state: Partial<QuickstartState>) => void;

  /**
   * Called when AFK timer expires
   */
  onAFKTimeout: () => void;

  /**
   * Called when Ad timer expires
   */
  onAdTimeout: () => void;
}

/**
 * Manager for AFK and Ad timers
 */
export class TimerManager {
  private config: TimerManagerConfig;
  private afkTimer?: NodeJS.Timeout;
  private adTimer?: NodeJS.Timeout;
  private afkStartTime?: number;
  private afkDuration?: number;
  private adStartTime?: number;
  private adDuration?: number;

  constructor(config: TimerManagerConfig) {
    this.config = config;
  }

  /**
   * Start AFK timer
   * Accounts for cumulative AFK time if theme is configured for it
   */
  public startAFKTimer(): void {
    // Warn if timer is already running (indicates duplicate start)
    if (this.afkTimer) {
      console.warn('[TimerManager] ⚠️ AFK timer already running, restarting...', new Error().stack);
    }

    // Clear any existing timer
    this.stopAFKTimer();

    const state = this.config.getState();
    const afkSeconds = state.afkTimeoutSeconds;
    const cumulativeAfkTime = state.cumulativeAfkTime;
    const afkBehavior = state.theme?.afkTimerBehavior || 'reset';

    // Calculate remaining time based on behavior
    let remainingSeconds = afkSeconds;
    if (afkBehavior === 'cumulative') {
      remainingSeconds = Math.max(0, afkSeconds - cumulativeAfkTime);
      console.log('[TimerManager] AFK timer:', remainingSeconds, 'seconds remaining (cumulative mode, used', cumulativeAfkTime, 'seconds)');
    } else {
      console.log('[TimerManager] AFK timer:', afkSeconds, 'seconds (reset mode)');
    }

    // If time already expired, trigger immediately
    if (remainingSeconds <= 0) {
      console.log('[TimerManager] AFK timer expired (no time remaining)');
      this.config.onAFKTimeout();
      return;
    }

    // Track start time and duration for calculating remaining time
    this.afkStartTime = Date.now();
    this.afkDuration = remainingSeconds;

    // Set timeout
    const timeoutMs = remainingSeconds * 1000;
    
    // Guard against invalid dates for logging
    const formatSafeDate = (timestamp: number): string => {
      if (!isFinite(timestamp) || timestamp < -8640000000000000 || timestamp > 8640000000000000) {
        return 'invalid date';
      }
      try {
        return new Date(timestamp).toISOString();
      } catch {
        return 'invalid date';
      }
    };
    
    console.log(`[TimerManager] ✅ AFK timer SET: ${remainingSeconds}s (${timeoutMs}ms) - will fire at ${formatSafeDate(Date.now() + timeoutMs)}`);

    this.afkTimer = setTimeout(() => {
      const expectedTime = this.afkStartTime ? formatSafeDate(this.afkStartTime + timeoutMs) : 'unknown';
      console.log(`[TimerManager] ⏰ AFK timer FIRED (expected at ${expectedTime})`);
      this.config.onAFKTimeout();
    }, timeoutMs);
  }

  /**
   * Stop AFK timer
   */
  public stopAFKTimer(): void {
    if (this.afkTimer) {
      const elapsed = this.afkStartTime ? (Date.now() - this.afkStartTime) / 1000 : 0;
      console.warn(`[TimerManager] ⚠️ AFK timer stopped prematurely (elapsed: ${elapsed.toFixed(1)}s / ${this.afkDuration}s)`, new Error().stack);
      clearTimeout(this.afkTimer);
      this.afkTimer = undefined;
    }
    this.afkStartTime = undefined;
    this.afkDuration = undefined;
  }

  /**
   * Start Ad timer (playtime limit)
   */
  public startAdTimer(): void {
    // Warn if timer is already running (indicates duplicate start)
    if (this.adTimer) {
      console.warn('[TimerManager] ⚠️ Ad timer already running, restarting...', new Error().stack);
    }

    // Clear any existing timer
    this.stopAdTimer();

    const state = this.config.getState();
    const adSeconds = state.adTimerSeconds;

    console.log('[TimerManager] Ad timer:', adSeconds, 'seconds');

    // Track start time and duration for calculating remaining time
    this.adStartTime = Date.now();
    this.adDuration = adSeconds;

    // Set timeout
    this.adTimer = setTimeout(() => {
      console.log('[TimerManager] Ad timer expired');
      this.config.onAdTimeout();
    }, adSeconds * 1000);
  }

  /**
   * Stop Ad timer
   */
  public stopAdTimer(): void {
    if (this.adTimer) {
      clearTimeout(this.adTimer);
      this.adTimer = undefined;
    }
    this.adStartTime = undefined;
    this.adDuration = undefined;
  }

  /**
   * Get remaining time for AFK timer in seconds
   * Returns the actual remaining time based on when the timer started
   */
  public getAFKRemainingTime(): number {
    if (!this.afkStartTime || !this.afkDuration) {
      return 0;
    }
    const elapsed = (Date.now() - this.afkStartTime) / 1000;
    const remaining = Math.max(0, this.afkDuration - elapsed);
    return Math.ceil(remaining); // Round up to show user-friendly countdown
  }

  /**
   * Get remaining time for Ad timer in seconds
   * Returns the actual remaining time based on when the timer started
   */
  public getAdRemainingTime(): number {
    if (!this.adStartTime || !this.adDuration) {
      return 0;
    }
    const elapsed = (Date.now() - this.adStartTime) / 1000;
    const remaining = Math.max(0, this.adDuration - elapsed);
    return Math.ceil(remaining); // Round up to show user-friendly countdown
  }

  /**
   * Calculate time spent in AFK state
   * Used for cumulative AFK tracking
   */
  public calculateAFKTime(sessionStartTime: number): number {
    return Math.floor((Date.now() - sessionStartTime) / 1000);
  }

  /**
   * Calculate total playtime
   */
  public calculatePlaytime(firstInputTime: number): number {
    return Date.now() - firstInputTime;
  }

  /**
   * Cleanup all timers
   */
  public cleanup(): void {
    this.stopAFKTimer();
    this.stopAdTimer();
  }
}

